import React from 'react';
import { Link } from 'react-router-dom';
import './landing.css';
import useLandingScroll from './useLandingScroll';
import useAppEntry from './useAppEntry';
import UpdateRequired from './UpdateRequired';
import openExternal from './openExternal';
import BookBNS from './art/BookBNS';
import BookBNSS from './art/BookBNSS';
import BookBSA from './art/BookBSA';
import CasePath from './art/CasePath';
import CtaCaseFile from './art/CtaCaseFile';
import HeroThana from './art/HeroThana';
import LogoMark from './art/LogoMark';
import SpotAccused from './art/SpotAccused';
import SpotComplainant from './art/SpotComplainant';
import SpotOfficer from './art/SpotOfficer';

// Generated from the LegalApp design canvas. Class names carry the "lp-" prefix
// so they never collide with the rest of the app's CSS.
export default function LandingPage() {
  const {
    rootRef, navTo, dots, railCls, atStart, atEnd, goPrev, goNext,
    navHome, navWhy, navHow, navWho, menuHome, menuWhy, menuHow, menuWho,
    menuOpen, menuClosed, menuExpanded, menuLabel, toggleMenu,
  } = useLandingScroll();
  const { signIn, updateRequired, getLatestVersion } = useAppEntry();
  return (
    <div className="lp-la" ref={rootRef}>
      <header className="lp-nav">
        <div className="lp-wrap lp-nav-in" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "24px", minHeight: "76px" }}>
          <a className="lp-brand" href="#top" style={{ display: "flex", alignItems: "center", gap: "12px", textDecoration: "none" }} aria-label="LegalApp home" onClick={navTo}>
            <LogoMark />
            <span className="lp-d lp-brand-word" style={{ fontSize: "24px", fontWeight: "700", letterSpacing: "-0.01em", color: "#0B1B3F" }}>
              {"Legal"}
              <span style={{ color: "#1D5BD8" }}>
                {"App"}
              </span>
            </span>
          </a>
          <nav className="lp-nav-links" aria-label="Main" style={{ display: "flex", alignItems: "center", gap: "30px" }}>
            <a className={navHome} href="#top" onClick={navTo}>
              {"Home"}
            </a>
            <a className={navWhy} href="#glance" onClick={navTo}>
              {"Why LegalApp"}
            </a>
            <a className={navHow} href="#how" onClick={navTo}>
              {"How it works"}
            </a>
            <a className={navWho} href="#who" onClick={navTo}>
              {"Who it's for"}
            </a>
            <Link to="/demos">
              {"Demos"}
            </Link>
          </nav>
          <div className="lp-nav-act" style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <a href="/login" onClick={signIn} className="lp-nav-signin">
              {"Sign in"}
            </a>
            <a className="lp-btn lp-btn-primary lp-btn-sm" href="https://wa.me/919582822225?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20of%20LegalApp." target="_blank" rel="noopener noreferrer" onClick={openExternal} title="Book a demo on WhatsApp">
              {"Book a demo"}
            </a>
            <button className="lp-menu-btn" type="button" aria-label={menuLabel} aria-expanded={menuExpanded} aria-controls="site-menu" onClick={toggleMenu} style={{ width: "44px", height: "44px", alignItems: "center", justifyContent: "center", border: "1px solid #DCE4F2", borderRadius: "12px", background: "#FFFFFF", color: "#0B1B3F", cursor: "pointer" }}>
              {menuOpen && (
                <>
                  <svg viewBox="0 0 24 24" style={{ width: "22px", height: "22px" }} aria-hidden="true">
                    <path className="lp-ic" d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </>
              )}
              {menuClosed && (
                <>
                  <svg viewBox="0 0 24 24" style={{ width: "22px", height: "22px" }} aria-hidden="true">
                    <path className="lp-ic" d="M4 7h16M4 12h16M4 17h16" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
        {menuOpen && (
          <>
            <div className="lp-menu-panel" id="site-menu">
              <nav className="lp-wrap" aria-label="Menu" style={{ display: "flex", flexDirection: "column", paddingTop: "6px", paddingBottom: "14px" }}>
                <a className={menuHome} href="#top" onClick={navTo}>
                  {"Home"}
                </a>
                <a className={menuWhy} href="#glance" onClick={navTo}>
                  {"Why LegalApp"}
                </a>
                <a className={menuHow} href="#how" onClick={navTo}>
                  {"How it works"}
                </a>
                <a className={menuWho} href="#who" onClick={navTo}>
                  {"Who it's for"}
                </a>
                <Link to="/demos" className="lp-menu-link">
                  {"Demos"}
                </Link>
                <a href="/login" onClick={signIn} className="lp-menu-link lp-signin">
                  {"Sign in"}
                  <svg viewBox="0 0 24 24" style={{ width: "20px", height: "20px" }} aria-hidden="true">
                    <path className="lp-ic" d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4M10 16l4-4-4-4M14 12H4" />
                  </svg>
                </a>
              </nav>
            </div>
          </>
        )}
      </header>
      <section id="top" className="lp-snap" data-snap="Welcome" style={{ padding: "clamp(32px, 4vw, 48px) 0" }}>
        <div className="lp-wrap" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(480px, 100%), 1fr))", gap: "40px", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "22px", position: "relative", zIndex: "2" }}>
            <div className="lp-greet lp-d lp-rise lp-r1">
              <span className="lp-sr">
                {"Hello, Investigating Officer,"}
              </span>
              <div className="lp-greet-track" aria-hidden="true">
                <span lang="en">
                  {"Hello, Investigating Officer,"}
                </span>
                <span lang="hi">
                  {"नमस्ते, जाँच अधिकारी,"}
                </span>
                <span lang="pa">
                  {"ਸਤ ਸ੍ਰੀ ਅਕਾਲ, ਜਾਂਚ ਅਧਿਕਾਰੀ,"}
                </span>
                <span lang="bn">
                  {"নমস্কার, তদন্তকারী অফিসার,"}
                </span>
                <span lang="ta">
                  {"வணக்கம், புலனாய்வு அதிகாரி,"}
                </span>
                <span lang="te">
                  {"నమస్కారం, దర్యాప్తు అధికారి,"}
                </span>
                <span lang="mr">
                  {"नमस्कार, तपास अधिकारी,"}
                </span>
                <span lang="gu">
                  {"નમસ્તે, તપાસ અધિકારી,"}
                </span>
                <span lang="kn">
                  {"ನಮಸ್ಕಾರ, ತನಿಖಾ ಅಧಿಕಾರಿ,"}
                </span>
                <span lang="ml">
                  {"നമസ്കാരം, അന്വേഷണ ഉദ്യോഗസ്ഥൻ,"}
                </span>
                <span lang="en">
                  {"Hello, Investigating Officer,"}
                </span>
              </div>
            </div>
            <h1 className="lp-d lp-rise lp-r2" style={{ fontSize: "clamp(54px, 7.2vw, 96px)", lineHeight: "0.92", fontWeight: "700", letterSpacing: "-0.025em", color: "#1D5BD8", textWrap: "balance" }}>
              {"Investigate smarter."}
            </h1>
            <p className="lp-rise lp-r3" style={{ fontSize: "clamp(18px, 1.55vw, 20px)", lineHeight: "1.6", color: "#33405E", maxWidth: "560px" }}>
              <strong style={{ color: "#0B1B3F", fontWeight: "600" }}>
                {"LegalApp is your AI assistant, from the first complaint to the chargesheet."}
              </strong>
              {" It works the case alongside you, so no detail, witness or step is left out."}
            </p>
            <p className="lp-d lp-rise lp-r4" style={{ fontSize: "clamp(19px, 1.7vw, 23px)", fontWeight: "600", color: "#1D5BD8" }}>
              {"A stronger case file, from the first entry."}
            </p>
            <div className="lp-rise lp-r5" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px 28px" }}>
              <a className="lp-btn lp-btn-primary" href="https://wa.me/919582822225?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20of%20LegalApp." target="_blank" rel="noopener noreferrer" onClick={openExternal} title="Book a demo on WhatsApp">
                {"Book a demo"}
              </a>
              <a className="lp-link-arrow" href="#how" onClick={navTo}>
                {"See how it works"}
                <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }} aria-hidden="true">
                  <path className="lp-ic" d="M12 5v14M6 13l6 6 6-6" />
                </svg>
              </a>
            </div>
            <div className="lp-rise lp-r6" style={{ display: "flex", flexDirection: "column", gap: "6px", paddingTop: "16px", borderTop: "1px solid #DCE4F2", maxWidth: "560px" }}>
              <div className="lp-eyebrow" style={{ fontSize: "12px", color: "#5A6684" }}>
                {"Works in all major Indian languages"}
              </div>
              <div className="lp-langs lp-d">
                <span lang="hi">
                  {"हिन्दी"}
                </span>
                <span lang="bn">
                  {"বাংলা"}
                </span>
                <span lang="mr">
                  {"मराठी"}
                </span>
                <span lang="te">
                  {"తెలుగు"}
                </span>
                <span lang="ta">
                  {"தமிழ்"}
                </span>
                <span lang="gu">
                  {"ગુજરાતી"}
                </span>
                <span lang="ur" dir="rtl">
                  {"اردو"}
                </span>
                <span lang="kn">
                  {"ಕನ್ನಡ"}
                </span>
                <span lang="or">
                  {"ଓଡ଼ିଆ"}
                </span>
                <span lang="ml">
                  {"മലയാളം"}
                </span>
                <span lang="pa">
                  {"ਪੰਜਾਬੀ"}
                </span>
                <span lang="as">
                  {"অসমীয়া"}
                </span>
                <span lang="en">
                  {"English"}
                </span>
              </div>
            </div>
          </div>
          <div className="lp-hero-art lp-pop">
            <div className="lp-hero-halo" aria-hidden="true" />
            <HeroThana />
          </div>
        </div>
      </section>
      <section id="glance" className="lp-snap" data-snap="Why LegalApp" style={{ padding: "clamp(48px, 6vw, 72px) 0" }}>
        <div className="lp-wrap">
          <div className="lp-feat lp-flip" style={{ gap: "64px" }}>
            <div className="lp-feat-text">
              <div className="lp-eyebrow" style={{ color: "#1D5BD8" }}>
                {"Why LegalApp"}
              </div>
              <h2 className="lp-d" style={{ fontSize: "clamp(34px, 4vw, 54px)", lineHeight: "1.04", fontWeight: "700", letterSpacing: "-0.02em", color: "#0B1B3F", textWrap: "balance" }}>
                {"You work the case. We'll keep the file complete."}
              </h2>
              <p style={{ fontSize: "clamp(17px, 1.5vw, 20px)", color: "#5A6684", textWrap: "pretty" }}>
                {"Cases weaken where details slip — at intake, in the evidence gathered, in what witnesses say. LegalApp sits beside the investigating officer at each of those points."}
              </p>
              <a className="lp-link-arrow" href="#how" onClick={navTo}>
                {"See how it works"}
                <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }} aria-hidden="true">
                  <path className="lp-ic" d="M12 5v14M6 13l6 6 6-6" />
                </svg>
              </a>
            </div>
            <div className="lp-stage" style={{ padding: "32px 24px" }}>
              <CasePath />
            </div>
          </div>
        </div>
      </section>
      <section id="laws" className="lp-pattern-dark lp-snap" data-snap="The new laws" style={{ backgroundColor: "#0B1B3F", color: "#FFFFFF", padding: "clamp(72px, 9vw, 112px) 0" }}>
        <div className="lp-wrap" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(380px, 100%), 1fr))", gap: "56px", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            <div className="lp-eyebrow" style={{ color: "#F2A33A" }}>
              {"Built on the new criminal laws"}
            </div>
            <h2 className="lp-d" style={{ fontSize: "clamp(34px, 4vw, 52px)", lineHeight: "1.04", fontWeight: "700", letterSpacing: "-0.02em", color: "#FFFFFF", textWrap: "balance" }}>
              {"Every suggestion traces back to the statute."}
            </h2>
            <p style={{ fontSize: "18px", color: "#C3D5F8", maxWidth: "520px", textWrap: "pretty" }}>
              {"Procedure and evidence rules follow the three codes in force since 1 July 2024 — so what an officer files today matches what the court will read."}
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: "28px 20px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <BookBNS />
              <div className="lp-m" style={{ fontSize: "13px", color: "#F2A33A", letterSpacing: "0.08em" }}>
                {"BNS"}
              </div>
              <div className="lp-d" style={{ fontSize: "18px", fontWeight: "600", lineHeight: "1.2" }}>
                {"Bharatiya Nyaya Sanhita, 2023"}
              </div>
              <div style={{ fontSize: "14px", color: "#9FB3D9" }}>
                {"Offences and punishment"}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <BookBNSS />
              <div className="lp-m" style={{ fontSize: "13px", color: "#F2A33A", letterSpacing: "0.08em" }}>
                {"BNSS"}
              </div>
              <div className="lp-d" style={{ fontSize: "18px", fontWeight: "600", lineHeight: "1.2" }}>
                {"Bharatiya Nagarik Suraksha Sanhita, 2023"}
              </div>
              <div style={{ fontSize: "14px", color: "#9FB3D9" }}>
                {"Procedure and investigation"}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <BookBSA />
              <div className="lp-m" style={{ fontSize: "13px", color: "#F2A33A", letterSpacing: "0.08em" }}>
                {"BSA"}
              </div>
              <div className="lp-d" style={{ fontSize: "18px", fontWeight: "600", lineHeight: "1.2" }}>
                {"Bharatiya Sakshya Adhiniyam, 2023"}
              </div>
              <div style={{ fontSize: "14px", color: "#9FB3D9" }}>
                {"Evidence"}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="how" className="lp-how">
        <div id="overview" className="lp-snap" data-snap="How it works" style={{ padding: "56px 0" }}>
          <div className="lp-wrap" style={{ display: "flex", flexDirection: "column", gap: "36px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: "24px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: "720px" }}>
                <div className="lp-eyebrow" style={{ color: "#1D5BD8" }}>
                  {"How it works"}
                </div>
                <h2 className="lp-d" style={{ fontSize: "clamp(36px, 4.6vw, 60px)", lineHeight: "1.02", fontWeight: "700", letterSpacing: "-0.02em", color: "#0B1B3F", textWrap: "balance" }}>
                  {"One case, from FIR to chargesheet."}
                </h2>
                <p style={{ fontSize: "clamp(17px, 1.5vw, 20px)", color: "#5A6684", textWrap: "pretty" }}>
                  {"Follow a single theft case — a phone and ₹3,000 taken near Gate 2 of the main market — through the eleven steps LegalApp helps with."}
                </p>
              </div>
              <a className="lp-link-arrow" href="#step-01" onClick={navTo}>
                {"Start with step 1"}
                <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }} aria-hidden="true">
                  <path className="lp-ic" d="M12 5v14M6 13l6 6 6-6" />
                </svg>
              </a>
            </div>
            <div className="lp-phases">
              <div className="lp-phase">
                <div className="lp-lbl" style={{ padding: "0 2px 2px" }}>
                  {"Registration"}
                </div>
                <a className="lp-stepchip" href="#step-01" onClick={navTo}>
                  <span className="lp-m">
                    {"01"}
                  </span>
                  {"FIR drafting"}
                </a>
              </div>
              <div className="lp-phase">
                <div className="lp-lbl" style={{ padding: "0 2px 2px" }}>
                  {"Planning"}
                </div>
                <a className="lp-stepchip" href="#step-02" onClick={navTo}>
                  <span className="lp-m">
                    {"02"}
                  </span>
                  {"Investigation plan"}
                </a>
              </div>
              <div className="lp-phase">
                <div className="lp-lbl" style={{ padding: "0 2px 2px" }}>
                  {"Evidence"}
                </div>
                <a className="lp-stepchip" href="#step-03" onClick={navTo}>
                  <span className="lp-m">
                    {"03"}
                  </span>
                  {"Evidence"}
                </a>
                <a className="lp-stepchip" href="#step-04" onClick={navTo}>
                  <span className="lp-m">
                    {"04"}
                  </span>
                  {"Evidence analysis"}
                </a>
              </div>
              <div className="lp-phase">
                <div className="lp-lbl" style={{ padding: "0 2px 2px" }}>
                  {"Witnesses & accused"}
                </div>
                <a className="lp-stepchip" href="#step-05" onClick={navTo}>
                  <span className="lp-m">
                    {"05"}
                  </span>
                  {"Witnesses"}
                </a>
                <a className="lp-stepchip" href="#step-06" onClick={navTo}>
                  <span className="lp-m">
                    {"06"}
                  </span>
                  {"Witness examination"}
                </a>
                <a className="lp-stepchip" href="#step-07" onClick={navTo}>
                  <span className="lp-m">
                    {"07"}
                  </span>
                  {"Interrogation"}
                </a>
              </div>
              <div className="lp-phase">
                <div className="lp-lbl" style={{ padding: "0 2px 2px" }}>
                  {"Statements"}
                </div>
                <a className="lp-stepchip" href="#step-08" onClick={navTo}>
                  <span className="lp-m">
                    {"08"}
                  </span>
                  {"Statements"}
                </a>
                <a className="lp-stepchip" href="#step-09" onClick={navTo}>
                  <span className="lp-m">
                    {"09"}
                  </span>
                  {"Cross-check"}
                </a>
              </div>
              <div className="lp-phase">
                <div className="lp-lbl" style={{ padding: "0 2px 2px" }}>
                  {"Paperwork"}
                </div>
                <a className="lp-stepchip" href="#step-10" onClick={navTo}>
                  <span className="lp-m">
                    {"10"}
                  </span>
                  {"Notices"}
                </a>
                <a className="lp-stepchip" href="#step-11" onClick={navTo}>
                  <span className="lp-m">
                    {"11"}
                  </span>
                  {"Chargesheet"}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div id="step-01" className="lp-snap lp-step" data-snap="01 \u00b7 FIR drafting">
          <div className="lp-wrap">
            <div className="lp-feat">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-cur" />
                    <span className="lp-ph" />
                    <span className="lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                    <span />
                    <span className="lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"01"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"FIR DRAFTING"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"A complete FIR, from the first account."}
                </h3>
                <p>
                  {"A complainant's first account is rarely complete. LegalApp reads it as it comes in, asks in plain language for what the investigation will need — when, where, who, and who else saw it — and drafts the FIR from the answers."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Flags missing details before registration"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Asks in the complainant's own language"}
                    </span>
                  </li>
                </ul>
              </div>
              <div className="lp-stage">
                <div style={{ position: "relative", width: "100%", maxWidth: "440px" }}>
                  <div className="lp-miss" style={{ position: "absolute", top: "-13px", right: "18px", zIndex: "2" }}>
                    {"2 details missing"}
                  </div>
                  <div className="lp-ui">
                    <div className="lp-ui-head">
                      <div className="lp-ui-title">
                        {"Complaint"}
                      </div>
                      <div className="lp-lbl">
                        {"Theft · Draft FIR"}
                      </div>
                    </div>
                    <div className="lp-ui-row">
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="10" className="lp-s-done" />
                        <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                      </svg>
                      <div>
                        <div className="lp-lbl">
                          {"Date & time"}
                        </div>
                        <div className="lp-val">
                          {"12 Sep, about 9:40 pm"}
                        </div>
                      </div>
                    </div>
                    <div className="lp-ui-row">
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="10" className="lp-s-done" />
                        <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                      </svg>
                      <div>
                        <div className="lp-lbl">
                          {"Place"}
                        </div>
                        <div className="lp-val">
                          {"Main market, near Gate 2"}
                        </div>
                      </div>
                    </div>
                    <div className="lp-ui-row">
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="10" className="lp-s-done" />
                        <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                      </svg>
                      <div>
                        <div className="lp-lbl">
                          {"Property"}
                        </div>
                        <div className="lp-val">
                          {"Black mobile phone, ₹3,000 cash"}
                        </div>
                      </div>
                    </div>
                    <div className="lp-ui-row" style={{ background: "#FFFBF4" }}>
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="9" className="lp-s-warn" />
                        <path d="M10 5.6v5.2M10 14.2v.1" className="lp-s-warn-mark" />
                      </svg>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                        <div className="lp-lbl">
                          {"Description of accused"}
                        </div>
                        <span className="lp-miss">
                          {"Missing"}
                        </span>
                      </div>
                    </div>
                    <div className="lp-ui-row" style={{ background: "#FFFBF4" }}>
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="9" className="lp-s-warn" />
                        <path d="M10 5.6v5.2M10 14.2v.1" className="lp-s-warn-mark" />
                      </svg>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                        <div className="lp-lbl">
                          {"Witnesses"}
                        </div>
                        <span className="lp-miss">
                          {"Missing"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ position: "relative", margin: "-14px -10px 0 auto", width: "min(330px, 92%)", background: "#0B1B3F", color: "#FFFFFF", borderRadius: "20px 20px 6px 20px", padding: "16px 18px", display: "flex", flexDirection: "column", gap: "8px", boxShadow: "0 24px 40px -20px rgba(11,27,63,.6)" }}>
                    <div className="lp-m" style={{ fontSize: "11px", letterSpacing: "0.1em", color: "#F2A33A" }}>
                      {"LEGALAPP ASKS"}
                    </div>
                    <div lang="hi" style={{ fontFamily: "'Anek Devanagari', sans-serif", fontSize: "19px", fontWeight: "500", lineHeight: "1.4" }}>
                      {"क्या आपने उस व्यक्ति का चेहरा देखा? उसने क्या पहना था?"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#C3D5F8" }}>
                      {"Did you see the person's face? What were they wearing?"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-02" className="lp-snap lp-step" data-snap="02 \u00b7 Investigation plan">
          <div className="lp-wrap">
            <div className="lp-feat lp-flip">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-cur lp-ph" />
                    <span className="lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                    <span />
                    <span className="lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"02"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"INVESTIGATION PLAN"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"A plan for every case."}
                </h3>
                <p>
                  {"Each case gets a step-by-step investigation plan drawn from the SOP for that offence — scene visit, statements, seizures, forensics, final report — with the provision behind each step and when it's due."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Offence-wise SOPs, adapted to the facts"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Every step dated and tracked"}
                    </span>
                  </li>
                </ul>
                <Link to="/demos#investigation-plan" className="lp-link-arrow lp-demo-link">
                  <svg viewBox="0 0 24 24" style={{ width: "24px", height: "24px", flex: "none" }} aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="#1D5BD8" />
                    <path d="M10 8.2v7.6l6.2-3.8z" fill="#FFFFFF" />
                  </svg>
                  <span>
                    {"Watch the demo"}
                  </span>
                  <span className="lp-m lp-demo-dur">
                    {"4:02"}
                  </span>
                </Link>
              </div>
              <div className="lp-stage">
                <div className="lp-ui" style={{ width: "100%", maxWidth: "460px" }}>
                  <div className="lp-ui-head" style={{ flexDirection: "column", alignItems: "stretch", gap: "12px" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "4px 12px" }}>
                      <div className="lp-ui-title">
                        {"Investigation plan"}
                      </div>
                      <div className="lp-lbl">
                        {"FIR 0412/26 · BNS 303(2)"}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div className="lp-bar" style={{ flex: "1", position: "relative", overflow: "hidden" }}>
                        <div style={{ position: "absolute", left: "0", top: "0", bottom: "0", width: "42%", background: "#1D5BD8", borderRadius: "3px" }} />
                      </div>
                      <div className="lp-m" style={{ fontSize: "12px", color: "#33405E" }}>
                        {"2 of 6"}
                      </div>
                    </div>
                  </div>
                  <div className="lp-ui-row" style={{ gridTemplateColumns: "20px minmax(0, 1fr) auto" }}>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" className="lp-s-done" />
                      <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                    </svg>
                    <div>
                      <div className="lp-val">
                        {"Visit the scene, prepare a site plan"}
                      </div>
                    </div>
                    <div className="lp-lbl">
                      {"Day 1"}
                    </div>
                  </div>
                  <div className="lp-ui-row" style={{ gridTemplateColumns: "20px minmax(0, 1fr) auto" }}>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" className="lp-s-done" />
                      <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                    </svg>
                    <div>
                      <div className="lp-val">
                        {"Record the complainant's statement"}
                      </div>
                      <div className="lp-lbl" style={{ marginTop: "2px" }}>
                        {"BNSS 180"}
                      </div>
                    </div>
                    <div className="lp-lbl">
                      {"Day 1"}
                    </div>
                  </div>
                  <div className="lp-ui-row" style={{ gridTemplateColumns: "20px minmax(0, 1fr) auto", background: "#F2F6FE", boxShadow: "inset 3px 0 0 #1D5BD8" }}>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="9" className="lp-s-active" />
                      <path d="M10 4a6 6 0 0 1 0 12z" fill="#1D5BD8" />
                    </svg>
                    <div>
                      <div className="lp-val" style={{ fontWeight: "500" }}>
                        {"Search and seizure, recorded on video"}
                      </div>
                      <div className="lp-lbl" style={{ marginTop: "2px" }}>
                        {"BNSS 105"}
                      </div>
                    </div>
                    <div className="lp-tag" style={{ background: "#1D5BD8", color: "#FFFFFF" }}>
                      {"Today"}
                    </div>
                  </div>
                  <div className="lp-ui-row" style={{ gridTemplateColumns: "20px minmax(0, 1fr) auto" }}>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="9" className="lp-s-ring" />
                    </svg>
                    <div>
                      <div className="lp-val" style={{ color: "#33405E" }}>
                        {"Collect CCTV footage nearby"}
                      </div>
                    </div>
                    <div className="lp-lbl">
                      {"Day 3"}
                    </div>
                  </div>
                  <div className="lp-ui-row" style={{ gridTemplateColumns: "20px minmax(0, 1fr) auto" }}>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="9" className="lp-s-ring" />
                    </svg>
                    <div>
                      <div className="lp-val" style={{ color: "#33405E" }}>
                        {"Request a trace on the phone's IMEI"}
                      </div>
                    </div>
                    <div className="lp-lbl">
                      {"Day 3"}
                    </div>
                  </div>
                  <div className="lp-ui-row" style={{ gridTemplateColumns: "20px minmax(0, 1fr) auto" }}>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="9" className="lp-s-ring" />
                    </svg>
                    <div>
                      <div className="lp-val" style={{ color: "#33405E" }}>
                        {"File the final report"}
                      </div>
                      <div className="lp-lbl" style={{ marginTop: "2px" }}>
                        {"BNSS 193"}
                      </div>
                    </div>
                    <div className="lp-lbl">
                      {"Pending"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-03" className="lp-snap lp-step" data-snap="03 \u00b7 Evidence">
          <div className="lp-wrap">
            <div className="lp-feat">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-cur lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                    <span />
                    <span className="lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"03"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"EVIDENCE"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"Evidence the FIR didn't mention."}
                </h3>
                <p>
                  {"From the facts of the case, LegalApp lists the evidence worth collecting — including what the complaint never mentioned — and says why each item matters."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Suggestions beyond the FIR, each with a reason"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"One checklist from collection to court"}
                    </span>
                  </li>
                </ul>
                <Link to="/demos#evidence" className="lp-link-arrow lp-demo-link">
                  <svg viewBox="0 0 24 24" style={{ width: "24px", height: "24px", flex: "none" }} aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="#1D5BD8" />
                    <path d="M10 8.2v7.6l6.2-3.8z" fill="#FFFFFF" />
                  </svg>
                  <span>
                    {"Watch the demo"}
                  </span>
                  <span className="lp-m lp-demo-dur">
                    {"3:20"}
                  </span>
                </Link>
              </div>
              <div className="lp-stage">
                <div style={{ position: "relative", width: "100%", maxWidth: "460px" }}>
                  <div className="lp-miss" style={{ position: "absolute", top: "-13px", right: "18px", zIndex: "2" }}>
                    {"+3 not in the FIR"}
                  </div>
                  <div className="lp-ui">
                    <div className="lp-ui-head">
                      <div className="lp-ui-title">
                        {"Evidence to collect"}
                      </div>
                      <div className="lp-lbl">
                        {"FIR 0412/26"}
                      </div>
                    </div>
                    <div className="lp-ui-row" style={{ gridTemplateColumns: "36px minmax(0, 1fr) auto", alignItems: "center" }}>
                      <div className="lp-ico">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path className="lp-ic" d="M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h4" />
                        </svg>
                      </div>
                      <div>
                        <div className="lp-val">
                          {"Complainant's written complaint"}
                        </div>
                        <div className="lp-sub">
                          {"Account of the theft, signed"}
                        </div>
                      </div>
                      <span className="lp-tag lp-t-fir">
                        {"In FIR"}
                      </span>
                    </div>
                    <div className="lp-ui-row" style={{ gridTemplateColumns: "36px minmax(0, 1fr) auto", alignItems: "center" }}>
                      <div className="lp-ico lp-s">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path className="lp-ic" d="M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6M9 16h3" />
                        </svg>
                      </div>
                      <div>
                        <div className="lp-val">
                          {"Purchase bill with the IMEI"}
                        </div>
                        <div className="lp-sub">
                          {"Proves ownership; needed to trace the phone"}
                        </div>
                      </div>
                      <span className="lp-tag lp-t-new">
                        {"Suggested"}
                      </span>
                    </div>
                    <div className="lp-ui-row" style={{ gridTemplateColumns: "36px minmax(0, 1fr) auto", alignItems: "center" }}>
                      <div className="lp-ico lp-s">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path className="lp-ic" d="M3 7h12v10H3zM15 10.5l6-3v9l-6-3" />
                        </svg>
                      </div>
                      <div>
                        <div className="lp-val">
                          {"CCTV from the shop opposite Gate 2"}
                        </div>
                        <div className="lp-sub">
                          {"Covers 9:30 to 9:50 pm"}
                        </div>
                      </div>
                      <span className="lp-tag lp-t-new">
                        {"Suggested"}
                      </span>
                    </div>
                    <div className="lp-ui-row" style={{ gridTemplateColumns: "36px minmax(0, 1fr) auto", alignItems: "center" }}>
                      <div className="lp-ico lp-s">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path className="lp-ic" d="M12 11v10M8 21h8M8.5 7.5a5 5 0 0 1 7 0M5.7 4.7a9 9 0 0 1 12.6 0M12 10.2v.1" />
                        </svg>
                      </div>
                      <div>
                        <div className="lp-val">
                          {"Call records of the stolen number"}
                        </div>
                        <div className="lp-sub">
                          {"Where the phone went after the theft"}
                        </div>
                      </div>
                      <span className="lp-tag lp-t-new">
                        {"Suggested"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-04" className="lp-snap lp-step" data-snap="04 \u00b7 Evidence analysis">
          <div className="lp-wrap">
            <div className="lp-feat lp-flip">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-cur" />
                    <span className="lp-ph" />
                    <span />
                    <span />
                    <span className="lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"04"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"EVIDENCE ANALYSIS"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"Documents, photos, audio and video — analysed."}
                </h3>
                <p>
                  {"Upload what's been collected. LegalApp reads documents, examines photographs, goes through audio and video recordings, and pins every finding to the page, frame or minute it came from."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Every finding linked to its source"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"One view across all four kinds of evidence"}
                    </span>
                  </li>
                </ul>
              </div>
              <div className="lp-stage">
                <div className="lp-ui" style={{ width: "100%", maxWidth: "480px", overflow: "hidden" }}>
                  <svg viewBox="0 0 480 200" style={{ display: "block", width: "100%", height: "auto" }} role="img" aria-label="A CCTV frame with a person marked near the market gate">
                    <rect x="0" y="0" width="480" height="200" fill="#13254F" />
                    <polygon points="0,150 480,128 480,200 0,200" fill="#1B3264" />
                    <rect x="40" y="70" width="70" height="72" fill="#22396E" />
                    <rect x="40" y="62" width="78" height="12" fill="#2C4A86" />
                    <rect x="128" y="78" width="60" height="62" fill="#22396E" />
                    <rect x="124" y="70" width="68" height="11" fill="#2C4A86" />
                    <rect x="330" y="40" width="14" height="100" fill="#2C4A86" />
                    <rect x="430" y="36" width="14" height="96" fill="#2C4A86" />
                    <rect x="326" y="30" width="122" height="14" fill="#36579A" />
                    <circle cx="262" cy="92" r="10" fill="#9FB3D9" />
                    <path d="M247 108c10-6 20-6 30 0l5 42h-40z" fill="#9FB3D9" />
                    <rect x="236" y="72" width="54" height="86" rx="4" className="lp-det" />
                    <rect x="236" y="56" width="86" height="16" rx="3" fill="#F2A33A" />
                    <text x="242" y="68" className="lp-vf-t" fill="#0B1B3F">
                      {"GREY HOODIE"}
                    </text>
                    <circle cx="20" cy="20" r="5" fill="#F2A33A" />
                    <text x="32" y="24" className="lp-vf-t" fill="#C3D5F8">
                      {"CAM 2 · 12-09-2026 21:41:07"}
                    </text>
                  </svg>
                  <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    <div className="lp-files">
                      <div className="lp-file">
                        <b>
                          {"PDF"}
                        </b>
                        <span>
                          {"Purchase bill"}
                        </span>
                      </div>
                      <div className="lp-file">
                        <b>
                          {"JPG"}
                        </b>
                        <span>
                          {"Scene photos"}
                        </span>
                      </div>
                      <div className="lp-file">
                        <b>
                          {"MP3"}
                        </b>
                        <span>
                          {"Complainant call"}
                        </span>
                      </div>
                      <div className="lp-file">
                        <b>
                          {"MP4"}
                        </b>
                        <span>
                          {"CCTV, Gate 2"}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                        <div className="lp-val" style={{ fontSize: "14px" }}>
                          {"Person in a grey hoodie at stall 14, leaves towards Gate 2"}
                        </div>
                        <span className="lp-ev">
                          {"MP4 · 21:41"}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                        <div className="lp-val" style={{ fontSize: "14px" }}>
                          {"IMEI on the bill matches the number in the complaint"}
                        </div>
                        <span className="lp-ev">
                          {"PDF · p.1"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-05" className="lp-snap lp-step" data-snap="05 \u00b7 Witnesses">
          <div className="lp-wrap">
            <div className="lp-feat">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-cur lp-ph" />
                    <span />
                    <span />
                    <span className="lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"05"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"WITNESSES"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"Witnesses the FIR didn't name."}
                </h3>
                <p>
                  {"LegalApp works out who was likely to have seen or heard something — shopkeepers, guards, people nearby — and flags the ones the complaint left out, with the reason each one matters."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Named and unnamed witnesses in one list"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"What each witness can speak to"}
                    </span>
                  </li>
                </ul>
                <Link to="/demos#witnesses" className="lp-link-arrow lp-demo-link">
                  <svg viewBox="0 0 24 24" style={{ width: "24px", height: "24px", flex: "none" }} aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="#1D5BD8" />
                    <path d="M10 8.2v7.6l6.2-3.8z" fill="#FFFFFF" />
                  </svg>
                  <span>
                    {"Watch the demo"}
                  </span>
                  <span className="lp-m lp-demo-dur">
                    {"4:26"}
                  </span>
                </Link>
              </div>
              <div className="lp-stage">
                <div style={{ position: "relative", width: "100%", maxWidth: "460px" }}>
                  <div className="lp-miss" style={{ position: "absolute", top: "-13px", right: "18px", zIndex: "2" }}>
                    {"+3 not in the FIR"}
                  </div>
                  <div className="lp-ui">
                    <div className="lp-ui-head">
                      <div className="lp-ui-title">
                        {"Potential witnesses"}
                      </div>
                      <div className="lp-lbl">
                        {"FIR 0412/26"}
                      </div>
                    </div>
                    <div className="lp-ui-row" style={{ gridTemplateColumns: "36px minmax(0, 1fr) auto", alignItems: "center" }}>
                      <div className="lp-av">
                        {"RS"}
                      </div>
                      <div>
                        <div className="lp-val">
                          {"Ravi Sharma, complainant's friend"}
                        </div>
                        <div className="lp-sub">
                          {"Was with the complainant at 9:40 pm"}
                        </div>
                      </div>
                      <span className="lp-tag lp-t-fir">
                        {"In FIR"}
                      </span>
                    </div>
                    <div className="lp-ui-row" style={{ gridTemplateColumns: "36px minmax(0, 1fr) auto", alignItems: "center" }}>
                      <div className="lp-av lp-s">
                        {"SK"}
                      </div>
                      <div>
                        <div className="lp-val">
                          {"Shopkeeper, stall 14"}
                        </div>
                        <div className="lp-sub">
                          {"Person seen at the stall on CCTV"}
                        </div>
                      </div>
                      <span className="lp-tag lp-t-new">
                        {"Suggested"}
                      </span>
                    </div>
                    <div className="lp-ui-row" style={{ gridTemplateColumns: "36px minmax(0, 1fr) auto", alignItems: "center" }}>
                      <div className="lp-av lp-s">
                        {"SG"}
                      </div>
                      <div>
                        <div className="lp-val">
                          {"Security guard, Gate 2"}
                        </div>
                        <div className="lp-sub">
                          {"On duty at the exit, 6 pm to 2 am"}
                        </div>
                      </div>
                      <span className="lp-tag lp-t-new">
                        {"Suggested"}
                      </span>
                    </div>
                    <div className="lp-ui-row" style={{ gridTemplateColumns: "36px minmax(0, 1fr) auto", alignItems: "center" }}>
                      <div className="lp-av lp-s">
                        {"AS"}
                      </div>
                      <div>
                        <div className="lp-val">
                          {"Auto stand, Gate 2"}
                        </div>
                        <div className="lp-sub">
                          {"Drivers waiting at the exit that night"}
                        </div>
                      </div>
                      <span className="lp-tag lp-t-new">
                        {"Suggested"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-06" className="lp-snap lp-step" data-snap="06 \u00b7 Witness examination">
          <div className="lp-wrap">
            <div className="lp-feat lp-flip">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-cur" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"06"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"WITNESS EXAMINATION"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"The right questions for every witness."}
                </h3>
                <p>
                  {"For each witness, LegalApp prepares an examination — what to establish and in what order — built from the facts and the gaps in the case, in the language the witness speaks."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Questions tailored to each witness"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Ready in the witness's language"}
                    </span>
                  </li>
                </ul>
                <Link to="/demos#witnesses" className="lp-link-arrow lp-demo-link">
                  <svg viewBox="0 0 24 24" style={{ width: "24px", height: "24px", flex: "none" }} aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="#1D5BD8" />
                    <path d="M10 8.2v7.6l6.2-3.8z" fill="#FFFFFF" />
                  </svg>
                  <span>
                    {"Watch the demo"}
                  </span>
                  <span className="lp-m lp-demo-dur">
                    {"4:26"}
                  </span>
                </Link>
              </div>
              <div className="lp-stage">
                <div className="lp-ui" style={{ width: "100%", maxWidth: "470px" }}>
                  <div className="lp-tabs">
                    <span>
                      {"Shopkeeper"}
                    </span>
                    <span className="lp-on">
                      {"Security guard"}
                    </span>
                    <span>
                      {"Ravi Sharma"}
                    </span>
                  </div>
                  <div className="lp-ui-head">
                    <div className="lp-ui-title">
                      {"Examination · Gate 2 guard"}
                    </div>
                    <div className="lp-lbl">
                      {"BNSS 180"}
                    </div>
                  </div>
                  <div style={{ padding: "14px 20px 18px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                      <span className="lp-qn">
                        {"1"}
                      </span>
                      <div className="lp-val" style={{ flex: "1" }}>
                        {"What time did your shift start on 12 September?"}
                      </div>
                      <span className="lp-tag lp-t-ink">
                        {"Timeline"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                      <span className="lp-qn">
                        {"2"}
                      </span>
                      <div style={{ flex: "1" }}>
                        <div className="lp-val">
                          {"Did anyone leave through Gate 2 between 9:30 and 9:50 pm?"}
                        </div>
                        <div lang="hi" style={{ fontFamily: "'Anek Devanagari', sans-serif", fontSize: "16px", color: "#1648B0", marginTop: "4px" }}>
                          {"क्या रात 9:30 से 9:50 के बीच कोई गेट 2 से बाहर गया?"}
                        </div>
                      </div>
                      <span className="lp-tag lp-t-ink">
                        {"Timeline"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                      <span className="lp-qn">
                        {"3"}
                      </span>
                      <div className="lp-val" style={{ flex: "1" }}>
                        {"What was that person wearing, and which way did they go?"}
                      </div>
                      <span className="lp-tag lp-t-ink">
                        {"Identity"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                      <span className="lp-qn">
                        {"4"}
                      </span>
                      <div className="lp-val" style={{ flex: "1" }}>
                        {"Does the gate keep a register or have a camera?"}
                      </div>
                      <span className="lp-tag lp-t-new">
                        {"Evidence"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-07" className="lp-snap lp-step" data-snap="07 \u00b7 Interrogation">
          <div className="lp-wrap">
            <div className="lp-feat">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-cur" />
                    <span className="lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"07"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"INTERROGATION"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"Interrogation, planned around the evidence."}
                </h3>
                <p>
                  {"For each accused person, LegalApp prepares questions grouped by theme and links each one to the evidence the officer can put to them."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Questions grouped by theme"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Each question tied to the evidence behind it"}
                    </span>
                  </li>
                </ul>
                <Link to="/demos#accused" className="lp-link-arrow lp-demo-link">
                  <svg viewBox="0 0 24 24" style={{ width: "24px", height: "24px", flex: "none" }} aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="#1D5BD8" />
                    <path d="M10 8.2v7.6l6.2-3.8z" fill="#FFFFFF" />
                  </svg>
                  <span>
                    {"Watch the demo"}
                  </span>
                  <span className="lp-m lp-demo-dur">
                    {"3:53"}
                  </span>
                </Link>
              </div>
              <div className="lp-stage">
                <div className="lp-ui" style={{ width: "100%", maxWidth: "470px" }}>
                  <div className="lp-ui-head">
                    <div className="lp-ui-title">
                      {"Interrogation plan · Accused 1"}
                    </div>
                    <div className="lp-lbl">
                      {"9 questions"}
                    </div>
                  </div>
                  <div style={{ padding: "14px 20px 18px", display: "flex", flexDirection: "column", gap: "14px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div className="lp-lbl" style={{ color: "#1D5BD8" }}>
                        {"Whereabouts"}
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                        <div className="lp-val">
                          {"Where were you between 9 and 10 pm on 12 September?"}
                        </div>
                        <span className="lp-ev">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path className="lp-ic" d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />
                          </svg>
                          {"CCTV 21:41"}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "12px", borderTop: "1px solid #EDF1F8" }}>
                      <div className="lp-lbl" style={{ color: "#1D5BD8" }}>
                        {"The phone"}
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                        <div className="lp-val">
                          {"How did this phone come to be with you?"}
                        </div>
                        <span className="lp-ev">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path className="lp-ic" d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />
                          </svg>
                          {"IMEI match"}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                        <div className="lp-val">
                          {"Who did you show it to, or offer it to?"}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "12px", borderTop: "1px solid #EDF1F8" }}>
                      <div className="lp-lbl" style={{ color: "#1D5BD8" }}>
                        {"Associates"}
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                        <div className="lp-val">
                          {"Who were you in touch with that night?"}
                        </div>
                        <span className="lp-ev">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path className="lp-ic" d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />
                          </svg>
                          {"Call records"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-08" className="lp-snap lp-step" data-snap="08 \u00b7 Statements">
          <div className="lp-wrap">
            <div className="lp-feat lp-flip">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done" />
                    <span className="lp-cur lp-ph" />
                    <span />
                    <span className="lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"08"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"STATEMENTS"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"Replies recorded. Case diary drafted."}
                </h3>
                <p>
                  {"Record what witnesses and the accused say, in their own words and language. LegalApp turns the replies into a draft case diary entry for the officer to review."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Replies kept word for word"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Case diary entries drafted from them"}
                    </span>
                  </li>
                </ul>
              </div>
              <div className="lp-stage">
                <div style={{ width: "100%", maxWidth: "470px", display: "flex", flexDirection: "column" }}>
                  <div className="lp-ui" style={{ width: "92%" }}>
                    <div className="lp-ui-head">
                      <div className="lp-ui-title">
                        {"Statement · Gate 2 guard"}
                      </div>
                      <div className="lp-lbl">
                        {"14 Sep, 11:20 am"}
                      </div>
                    </div>
                    <div style={{ padding: "14px 18px 18px", display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div style={{ fontSize: "14px", color: "#5A6684" }}>
                        <span className="lp-m" style={{ color: "#1D5BD8" }}>
                          {"Q."}
                        </span>
                        {" Did anyone leave through Gate 2 between 9:30 and 9:50 pm?"}
                      </div>
                      <div className="lp-quote">
                        <span className="lp-m" style={{ color: "#A85A0C" }}>
                          {"A."}
                        </span>
                        {" "}
                        <span lang="hi" style={{ fontFamily: "'Anek Devanagari', sans-serif", fontSize: "16px" }}>
                          {"हाँ, एक लड़का ग्रे हुडी में तेज़ी से बाहर निकला, करीब पौने दस बजे।"}
                        </span>
                        <div style={{ fontSize: "12.5px", color: "#5A6684", marginTop: "4px" }}>
                          {"Yes, a young man in a grey hoodie hurried out, at about 9:45."}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="lp-ui" style={{ width: "92%", margin: "-18px 0 0 auto", borderColor: "#C3D5F8" }}>
                    <div className="lp-ui-head">
                      <div className="lp-ui-title">
                        {"Case diary"}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span className="lp-lbl">
                          {"BNSS 192"}
                        </span>
                        <span className="lp-tag lp-t-fir">
                          {"Draft"}
                        </span>
                      </div>
                    </div>
                    <div style={{ padding: "14px 18px 18px", fontSize: "14px", color: "#33405E", lineHeight: "1.55" }}>
                      <span className="lp-m" style={{ fontSize: "12px", color: "#0B1B3F" }}>
                        {"14.09.2026, 11:20 am —"}
                      </span>
                      {" Recorded the statement of the security guard posted at Gate 2, who states that at about 9:45 pm on 12.09.2026 a young man in a grey hoodie hurried out through Gate 2…\n              "}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-09" className="lp-snap lp-step" data-snap="09 \u00b7 Cross-check">
          <div className="lp-wrap">
            <div className="lp-feat">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-cur" />
                    <span className="lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"09"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"CROSS-CHECK"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"Catch what doesn't add up."}
                </h3>
                <p>
                  {"LegalApp reads every statement side by side and flags discrepancies, contradictions, and replies that are vague or evasive — before the defence does."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Contradictions between witnesses, and with the evidence"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Vague and evasive replies flagged"}
                    </span>
                  </li>
                </ul>
              </div>
              <div className="lp-stage">
                <div className="lp-ui" style={{ width: "100%", maxWidth: "480px" }}>
                  <div className="lp-ui-head">
                    <div className="lp-ui-title">
                      {"Cross-check"}
                    </div>
                    <div className="lp-lbl">
                      {"6 statements"}
                    </div>
                  </div>
                  <div style={{ padding: "14px 18px 18px", display: "flex", flexDirection: "column", gap: "14px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                        <span className="lp-tag lp-t-new">
                          {"Contradiction"}
                        </span>
                        <span className="lp-sub" style={{ margin: "0" }}>
                          {"Time and manner of leaving"}
                        </span>
                      </div>
                      <div className="lp-pair" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)", gap: "8px", alignItems: "center" }}>
                        <div className="lp-quote">
                          <div className="lp-lbl" style={{ marginBottom: "4px" }}>
                            {"Guard · 14 Sep"}
                          </div>
                          {"“…hurried out at about 9:45 pm.”"}
                        </div>
                        <div className="lp-d lp-pair-ne" style={{ fontSize: "22px", fontWeight: "700", color: "#A85A0C" }}>
                          {"≠"}
                        </div>
                        <div className="lp-quote">
                          <div className="lp-lbl" style={{ marginBottom: "4px" }}>
                            {"Shopkeeper · 15 Sep"}
                          </div>
                          {"“…walked off slowly, around 9:30.”"}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "12px", borderTop: "1px solid #EDF1F8" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                        <span className="lp-tag lp-t-ink">
                          {"Evasive"}
                        </span>
                        <span className="lp-sub" style={{ margin: "0" }}>
                          {"Asked 3 times, no direct answer"}
                        </span>
                      </div>
                      <div className="lp-quote">
                        <div className="lp-lbl" style={{ marginBottom: "4px" }}>
                          {"Accused 1 · 16 Sep"}
                        </div>
                        {"“I don't remember where I was.”"}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingTop: "12px", borderTop: "1px solid #EDF1F8" }}>
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="10" className="lp-s-done" />
                        <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                      </svg>
                      <div className="lp-val" style={{ fontSize: "14px" }}>
                        {"The guard's 9:45 pm matches CCTV at 21:43"}
                      </div>
                      <span className="lp-tag lp-t-fir" style={{ marginLeft: "auto" }}>
                        {"Consistent"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-10" className="lp-snap lp-step" data-snap="10 \u00b7 Notices">
          <div className="lp-wrap">
            <div className="lp-feat lp-flip">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-cur lp-ph" />
                    <span />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"10"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"NOTICES"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"Notices, pre-filled."}
                </h3>
                <p>
                  {"Notices to witnesses and the accused come pre-filled from what's already on record. Nothing is typed twice. The officer reviews, edits and signs."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Every field traced to the case record"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Edit freely before anything is final"}
                    </span>
                  </li>
                </ul>
              </div>
              <div className="lp-stage">
                <div style={{ position: "relative", width: "100%", maxWidth: "420px", height: "430px" }}>
                  <div className="lp-paper" style={{ left: "0", right: "30%", top: "22px", height: "290px", transform: "rotate(-7deg)", padding: "18px 20px" }}>
                    <div className="lp-lbl">
                      {"BNSS 179"}
                    </div>
                    <div className="lp-d" style={{ fontSize: "17px", fontWeight: "600", color: "#0B1B3F", marginTop: "4px" }}>
                      {"Notice to a witness"}
                    </div>
                  </div>
                  <div className="lp-paper" style={{ left: "36%", right: "-2%", top: "4px", height: "290px", transform: "rotate(5deg)", padding: "18px 20px", background: "#FBFCFF" }}>
                    <div className="lp-lbl" style={{ textAlign: "right" }}>
                      {"BNSS 94"}
                    </div>
                    <div className="lp-d" style={{ fontSize: "17px", fontWeight: "600", color: "#0B1B3F", marginTop: "4px", textAlign: "right" }}>
                      {"Notice to produce"}
                    </div>
                  </div>
                  <div className="lp-paper" style={{ left: "4%", right: "4%", top: "92px", bottom: "0", padding: "22px", display: "flex", flexDirection: "column", gap: "13px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                      <span className="lp-m" style={{ fontSize: "12px", fontWeight: "500", color: "#1D5BD8", letterSpacing: "0.06em" }}>
                        {"BNSS 35(3)"}
                      </span>
                      <span className="lp-tag lp-t-fir">
                        {"Draft"}
                      </span>
                    </div>
                    <div className="lp-d" style={{ fontSize: "22px", fontWeight: "700", color: "#0B1B3F", lineHeight: "1.1" }}>
                      {"Notice of appearance"}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", gap: "8px 14px", fontSize: "14px", alignItems: "baseline" }}>
                      <span className="lp-lbl">
                        {"Case"}
                      </span>
                      <span>
                        <span className="lp-fill">
                          {"FIR 0412/26 · BNS 303(2)"}
                        </span>
                      </span>
                      <span className="lp-lbl">
                        {"Appear on"}
                      </span>
                      <span>
                        <span className="lp-fill">
                          {"18 Sep, 11:00 am"}
                        </span>
                      </span>
                      <span className="lp-lbl">
                        {"At"}
                      </span>
                      <span>
                        <span className="lp-fill">
                          {"PS Sector 21"}
                        </span>
                      </span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "2px" }}>
                      <div className="lp-bar" style={{ width: "100%" }} />
                      <div className="lp-bar" style={{ width: "92%" }} />
                      <div className="lp-bar" style={{ width: "64%" }} />
                    </div>
                    <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                      <span className="lp-m" style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#33405E" }}>
                        <span className="lp-dot" />
                        {"6 fields from record"}
                      </span>
                      <span className="lp-btn lp-btn-saffron" style={{ minHeight: "40px", padding: "0 18px", fontSize: "14px" }}>
                        {"Review & sign"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="step-11" className="lp-snap lp-step" data-snap="11 \u00b7 Chargesheet" style={{ borderBottom: "0" }}>
          <div className="lp-wrap">
            <div className="lp-feat">
              <div className="lp-feat-text">
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="lp-track" aria-hidden="true">
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-done" />
                    <span className="lp-done lp-ph" />
                    <span className="lp-cur" />
                  </div>
                  <div className="lp-step-no">
                    <span>
                      {"11"}
                    </span>
                    <i>
                      {"/ 11"}
                    </i>
                    <b />
                    <span>
                      {"CHARGESHEET"}
                    </span>
                  </div>
                </div>
                <h3>
                  {"From case diary to chargesheet."}
                </h3>
                <p>
                  {"When the investigation is complete, LegalApp drafts the chargesheet from the case diary — parties, offences, witnesses, evidence and the brief facts — ready for the officer's review."}
                </p>
                <ul className="lp-ticks">
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Built from the case diary, entry by entry"}
                    </span>
                  </li>
                  <li>
                    <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                      <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                    </svg>
                    <span>
                      {"Every witness and exhibit accounted for"}
                    </span>
                  </li>
                </ul>
                <Link to="/demos#chargesheet" className="lp-link-arrow lp-demo-link">
                  <svg viewBox="0 0 24 24" style={{ width: "24px", height: "24px", flex: "none" }} aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="#1D5BD8" />
                    <path d="M10 8.2v7.6l6.2-3.8z" fill="#FFFFFF" />
                  </svg>
                  <span>
                    {"Watch the demo"}
                  </span>
                  <span className="lp-m lp-demo-dur">
                    {"3:44"}
                  </span>
                </Link>
              </div>
              <div className="lp-stage">
                <div style={{ width: "100%", maxWidth: "500px", display: "grid", gridTemplateColumns: "minmax(0, 0.8fr) auto minmax(0, 1.2fr)", gap: "12px", alignItems: "center" }} className="lp-pair">
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div className="lp-lbl" style={{ paddingLeft: "2px" }}>
                      {"Case diary"}
                    </div>
                    <div className="lp-ui" style={{ padding: "10px 12px", borderRadius: "12px" }}>
                      <div className="lp-m" style={{ fontSize: "11px", color: "#1D5BD8" }}>
                        {"CD 1 · 12 Sep"}
                      </div>
                      <div className="lp-val" style={{ fontSize: "13px" }}>
                        {"FIR registered"}
                      </div>
                    </div>
                    <div className="lp-ui" style={{ padding: "10px 12px", borderRadius: "12px" }}>
                      <div className="lp-m" style={{ fontSize: "11px", color: "#1D5BD8" }}>
                        {"CD 4 · 14 Sep"}
                      </div>
                      <div className="lp-val" style={{ fontSize: "13px" }}>
                        {"Statements recorded"}
                      </div>
                    </div>
                    <div className="lp-ui" style={{ padding: "10px 12px", borderRadius: "12px" }}>
                      <div className="lp-m" style={{ fontSize: "11px", color: "#1D5BD8" }}>
                        {"CD 9 · 20 Sep"}
                      </div>
                      <div className="lp-val" style={{ fontSize: "13px" }}>
                        {"Phone recovered"}
                      </div>
                    </div>
                  </div>
                  <svg className="lp-pair-ne lp-pair-arrow" viewBox="0 0 40 24" style={{ width: "36px", height: "22px", color: "#1D5BD8" }} aria-hidden="true">
                    <path className="lp-ic" d="M2 12h34M28 5l8 7-8 7" />
                  </svg>
                  <div className="lp-ui">
                    <div className="lp-ui-head" style={{ padding: "14px 16px" }}>
                      <div className="lp-ui-title" style={{ fontSize: "17px" }}>
                        {"Chargesheet"}
                      </div>
                      <div className="lp-lbl">
                        {"BNSS 193"}
                      </div>
                    </div>
                    <div className="lp-ui-row" style={{ padding: "9px 16px" }}>
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="10" className="lp-s-done" />
                        <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                      </svg>
                      <div className="lp-val" style={{ fontSize: "14px" }}>
                        {"Parties and their details"}
                      </div>
                    </div>
                    <div className="lp-ui-row" style={{ padding: "9px 16px" }}>
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="10" className="lp-s-done" />
                        <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                      </svg>
                      <div className="lp-val" style={{ fontSize: "14px" }}>
                        {"Offence · BNS 303(2)"}
                      </div>
                    </div>
                    <div className="lp-ui-row" style={{ padding: "9px 16px" }}>
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="10" className="lp-s-done" />
                        <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                      </svg>
                      <div className="lp-val" style={{ fontSize: "14px" }}>
                        {"Witnesses · 4"}
                      </div>
                    </div>
                    <div className="lp-ui-row" style={{ padding: "9px 16px" }}>
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="10" className="lp-s-done" />
                        <path d="M6 10.3l2.6 2.6 5.4-5.6" className="lp-s-check" />
                      </svg>
                      <div className="lp-val" style={{ fontSize: "14px" }}>
                        {"Evidence · 5 items"}
                      </div>
                    </div>
                    <div className="lp-ui-row" style={{ padding: "9px 16px" }}>
                      <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                        <circle cx="10" cy="10" r="9" className="lp-s-active" />
                        <path d="M10 4a6 6 0 0 1 0 12z" fill="#1D5BD8" />
                      </svg>
                      <div className="lp-val" style={{ fontSize: "14px", fontWeight: "500" }}>
                        {"Brief facts of the case"}
                      </div>
                    </div>
                    <div style={{ padding: "12px 16px 14px" }}>
                      <span className="lp-btn lp-btn-saffron" style={{ minHeight: "40px", padding: "0 18px", fontSize: "14px", width: "100%" }}>
                        {"Review chargesheet"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="who" className="lp-snap" data-snap="Who it's for" style={{ padding: "36px 0" }}>
        <div className="lp-wrap" style={{ display: "flex", flexDirection: "column", gap: "26px" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", textAlign: "center" }}>
            <div className="lp-eyebrow" style={{ color: "#1D5BD8" }}>
              {"Who it's for"}
            </div>
            <h2 className="lp-d" style={{ fontSize: "clamp(32px, 3.8vw, 50px)", lineHeight: "1.04", fontWeight: "700", letterSpacing: "-0.02em", color: "#0B1B3F", textWrap: "balance" }}>
              {"Built for the officer. Fair to everyone in the case."}
            </h2>
          </div>
          <div className="lp-aud">
            <div className="lp-aud-col">
              <div className="lp-aud-art">
                <SpotOfficer />
              </div>
              <div className="lp-eyebrow" style={{ color: "#1D5BD8" }}>
                {"For investigating officers"}
              </div>
              <h3 className="lp-d" style={{ fontSize: "clamp(22px, 1.9vw, 27px)", lineHeight: "1.12", fontWeight: "700", color: "#0B1B3F" }}>
                {"Less time on the file, more on the case."}
              </h3>
              <ul className="lp-ticks">
                <li>
                  <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                    <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                  </svg>
                  <span>
                    {"A plan for every offence, step by step"}
                  </span>
                </li>
                <li>
                  <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                    <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                  </svg>
                  <span>
                    {"Questions for every witness and accused"}
                  </span>
                </li>
                <li>
                  <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                    <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                  </svg>
                  <span>
                    {"Notices, case diary and chargesheet drafted"}
                  </span>
                </li>
              </ul>
              <div style={{ marginTop: "auto", paddingTop: "4px" }}>
                <a className="lp-btn lp-btn-primary" href="https://wa.me/919582822225?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20of%20LegalApp%20for%20our%20police%20station." target="_blank" rel="noopener noreferrer" onClick={openExternal} title="Book a demo on WhatsApp">
                  {"Book a station demo"}
                </a>
              </div>
            </div>
            <div className="lp-aud-col">
              <div className="lp-aud-art">
                <SpotComplainant />
              </div>
              <div className="lp-eyebrow" style={{ color: "#1D5BD8" }}>
                {"For complainants"}
              </div>
              <h3 className="lp-d" style={{ fontSize: "clamp(22px, 1.9vw, 27px)", lineHeight: "1.12", fontWeight: "700", color: "#0B1B3F" }}>
                {"Tell it once. Tell it completely."}
              </h3>
              <ul className="lp-ticks">
                <li>
                  <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                    <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                  </svg>
                  <span>
                    {"Guided questions in all major Indian languages"}
                  </span>
                </li>
                <li>
                  <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                    <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                  </svg>
                  <span>
                    {"Nothing important left out of the FIR"}
                  </span>
                </li>
                <li>
                  <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                    <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                  </svg>
                  <span>
                    {"A clear record of what was reported"}
                  </span>
                </li>
              </ul>
              <div style={{ marginTop: "auto", paddingTop: "4px" }}>
                <a className="lp-btn lp-btn-outline" href="https://wa.me/919582822225?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20of%20LegalApp%20for%20complainant%27s%20counsel." target="_blank" rel="noopener noreferrer" onClick={openExternal} title="Book a demo on WhatsApp">
                  {"Book a demo"}
                </a>
              </div>
            </div>
            <div className="lp-aud-col">
              <div className="lp-aud-art">
                <SpotAccused />
              </div>
              <div className="lp-eyebrow" style={{ color: "#1D5BD8" }}>
                {"For the accused"}
              </div>
              <h3 className="lp-d" style={{ fontSize: "clamp(22px, 1.9vw, 27px)", lineHeight: "1.12", fontWeight: "700", color: "#0B1B3F" }}>
                {"Their side, on record."}
              </h3>
              <ul className="lp-ticks">
                <li>
                  <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                    <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                  </svg>
                  <span>
                    {"A notice to appear where arrest isn't needed"}
                  </span>
                </li>
                <li>
                  <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                    <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                  </svg>
                  <span>
                    {"Replies recorded word for word, in their language"}
                  </span>
                </li>
                <li>
                  <svg viewBox="0 0 20 20" className="lp-tick" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#F2A33A" />
                    <path d="M5.6 10.2l3 3 5.8-6.2" className="lp-tick-path" />
                  </svg>
                  <span>
                    {"Their account checked against the evidence too"}
                  </span>
                </li>
              </ul>
              <div style={{ marginTop: "auto", paddingTop: "4px" }}>
                <a className="lp-btn lp-btn-outline" href="https://wa.me/919582822225?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20of%20LegalApp%20for%20defence%20counsel." target="_blank" rel="noopener noreferrer" onClick={openExternal} title="Book a demo on WhatsApp">
                  {"Book a demo"}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="demo" className="lp-snap" data-snap="Book a demo" style={{ padding: "clamp(48px, 6vw, 72px) 0" }}>
        <div className="lp-wrap">
          <div className="lp-pattern-dark" style={{ backgroundColor: "#0B1B3F", borderRadius: "36px", padding: "clamp(36px, 5vw, 72px)", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))", gap: "40px", alignItems: "center", overflow: "hidden" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
              <h2 className="lp-d" style={{ fontSize: "clamp(34px, 4.2vw, 54px)", lineHeight: "1.04", fontWeight: "700", letterSpacing: "-0.02em", color: "#FFFFFF", textWrap: "balance" }}>
                {"Every complaint deserves a complete investigation."}
              </h2>
              <p style={{ fontSize: "18px", color: "#C3D5F8", maxWidth: "500px" }}>
                {"See LegalApp work through a case from your own station."}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px 24px" }}>
                <a className="lp-btn lp-btn-saffron" href="https://wa.me/919582822225?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20of%20LegalApp." target="_blank" rel="noopener noreferrer" onClick={openExternal} title="Book a demo on WhatsApp">
                  {"Book a demo"}
                </a>
                <a href="mailto:contact@legalapp.in" style={{ color: "#FFFFFF", fontWeight: "500", textDecoration: "underline", textUnderlineOffset: "4px" }}>
                  {"or write to contact@legalapp.in"}
                </a>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <CtaCaseFile />
            </div>
          </div>
        </div>
      </section>
      <footer id="contact" className="lp-foot" style={{ borderTop: "1px solid #DCE4F2", padding: "48px 0 32px", background: "#FFFFFF" }}>
        <div className="lp-wrap" style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))", gap: "36px 24px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <LogoMark />
                <span className="lp-d" style={{ fontSize: "22px", fontWeight: "700", color: "#0B1B3F" }}>
                  {"Legal"}
                  <span style={{ color: "#1D5BD8" }}>
                    {"App"}
                  </span>
                </span>
              </div>
              <p style={{ fontSize: "15px", color: "#5A6684", maxWidth: "260px" }}>
                {"The AI assistant for investigation officers."}
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "15px" }}>
              <div className="lp-lbl" style={{ marginBottom: "4px" }}>
                {"Product"}
              </div>
              <a href="#glance" onClick={navTo}>
                {"Why LegalApp"}
              </a>
              <a href="#how" onClick={navTo}>
                {"How it works"}
              </a>
              <a href="#who" onClick={navTo}>
                {"Who it's for"}
              </a>
              <Link to="/demos">
                {"Demos"}
              </Link>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "15px" }}>
              <div className="lp-lbl" style={{ marginBottom: "4px" }}>
                {"Company"}
              </div>
              <a href="https://www.linkedin.com/company/106991376/" target="_blank" rel="noopener noreferrer" onClick={openExternal} title="About LegalApp on LinkedIn">
                {"About"}
              </a>
              <Link to="/privacy">
                {"Privacy"}
              </Link>
              <Link to="/terms">
                {"Terms of Use"}
              </Link>
              <a href="https://wa.me/919582822225" target="_blank" rel="noopener noreferrer" onClick={openExternal} title="Chat with the LegalApp team on WhatsApp (+91 95828 22225)">
                {"Support"}
              </a>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "15px" }}>
              <div className="lp-lbl" style={{ marginBottom: "4px" }}>
                {"Contact"}
              </div>
              <a href="mailto:contact@legalapp.in">
                {"contact@legalapp.in"}
              </a>
              <a href="https://www.linkedin.com/company/106991376/" target="_blank" rel="noopener noreferrer" onClick={openExternal} title="LegalApp on LinkedIn">
                {"LinkedIn"}
              </a>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "12px", paddingTop: "24px", borderTop: "1px solid #EDF1F8", fontSize: "14px", color: "#5A6684" }}>
            <span>
              {"© 2026 LexOnAlgo Advisory Pvt. Ltd. All rights reserved."}
            </span>
            <span>
              {"Made in India"}
            </span>
          </div>
        </div>
      </footer>
      <nav className={railCls} aria-label="Sections on this page">
        <button className="lp-rail-btn" type="button" aria-label="Previous section" onClick={goPrev} disabled={atStart}>
          <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }} aria-hidden="true">
            <path className="lp-ic" d="M12 19V5M6 11l6-6 6 6" />
          </svg>
        </button>
        <div className="lp-rail-dots">
          {dots.map((item) => (
              <a key={item.href} className={item.cls} href={item.href} aria-label={item.label} aria-current={item.current} onClick={navTo}>
                <span className="lp-tip">
                  {item.label}
                </span>
              </a>
          ))}
        </div>
        <button className="lp-rail-btn" type="button" aria-label="Next section" onClick={goNext} disabled={atEnd}>
          <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }} aria-hidden="true">
            <path className="lp-ic" d="M12 5v14M6 13l6 6 6-6" />
          </svg>
        </button>
      </nav>
      {updateRequired && <UpdateRequired onUpdate={getLatestVersion} />}
    </div>
  );
}
